import React, { useState, useEffect } from 'react';
import { ToggleLeft, ToggleRight, Star, Filter, Tv, CheckSquare, Square, Trash2 } from 'lucide-react';
import * as api from '../services/api';
import CategoryMultiSelect from '../components/CategoryMultiSelect';

const isCustomer = () => JSON.parse(localStorage.getItem('user') || '{}')?.role === 'customer';

function Series() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [pagination, setPagination] = useState({ limit: (() => { try { const n = parseInt(sessionStorage.getItem('iptv-page-size') || '50', 10); return [50, 100, 200].includes(n) ? n : 50; } catch { return 50; } })(), offset: 0, total: 0 });
  const [filters, setFilters] = useState({
    provider_id: '',
    categories: [],
    enabled: '',
    search: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Reload when provider filter changes
  useEffect(() => {
    if (filters.provider_id) {
      setLoading(true);
      loadData();
    }
  }, [filters.provider_id]);
  // Reload when page changes
  useEffect(() => {
    setLoading(true);
    loadData();
  }, [pagination.offset, pagination.limit]);

  const loadData = async () => {
    try {

      // Issue 117: categories[] → comma-separated for API
      const cleanFilters = Object.fromEntries(
        Object.entries(filters)
          .filter(([key, value]) => {
            if (key === 'categories') return Array.isArray(value) && value.length > 0;
            return value !== '';
          })
          .map(([key, value]) =>
            key === 'categories' ? ['categories', value.join(',')] : [key, value]
          )
      );

      const categoryFilters = filters.provider_id ? { provider_id: filters.provider_id } : {};
      const providersPromise = isCustomer()
        ? api.customerGetProfile().then(r => ({ data: (r.data.provider_lines || []).map(pl => ({ id: pl.id, name: pl.name })) })).catch(() => ({ data: [] }))
        : api.getProviders();

      const seriesPromise = isCustomer()
        ? api.customerGetSeries({ ...cleanFilters, limit: pagination.limit, offset: pagination.offset })
        : api.getSeries({ ...cleanFilters, limit: pagination.limit, offset: pagination.offset });

      const categoriesPromise = isCustomer()
        ? api.customerGetCategories('series', filters.provider_id || undefined)
        : api.getSeriesCategories(categoryFilters);

      const [seriesRes, providersRes, categoriesRes] = await Promise.all([
        seriesPromise,
        providersPromise,
        categoriesPromise
      ]);
      const serData = seriesRes.data?.data || seriesRes.data;
      setSeries(Array.isArray(serData) ? serData : []);
      setPagination(prev => ({ ...prev, total: seriesRes.data?.total || (Array.isArray(serData) ? serData.length : 0) }));
      setProviders(Array.isArray(providersRes.data) ? providersRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setSelectedIds([]);
      if (isCustomer()) {
        try {
          const favRes = await api.customerGetFavorites();
          setFavoriteIds(new Set((favRes.data || []).filter(f => f.item_type === 'series').map(f => f.item_id)));
        } catch (e) { setFavoriteIds(new Set()); }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setSeries([]);
      setProviders([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (key, value) => {
    const newFilters = { ...filters, [key]: value };

    // If provider changes, reset category and reload categories for that provider
    if (key === 'provider_id') {
      newFilters.categories = [];
      setFilters(newFilters);

      // Load categories for the selected provider
      try {
        const isCust = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'customer';
        const categoriesRes = isCust
          ? await api.customerGetCategories('series', filters.provider_id || undefined)
          : await api.getSeriesCategories(value ? { provider_id: value } : {});
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories([]);
      }
    } else {
      setFilters(newFilters);
    }
  };

  const handlePageSizeChange = (n) => {
    const limit = parseInt(n, 10);
    try { sessionStorage.setItem('iptv-page-size', String(limit)); } catch (_) {}
    setLoading(true);
    setPagination(prev => ({ ...prev, limit, offset: 0 }));
  };

  const applyFilters = () => {
    setLoading(true);
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadData();
  };


  const toggleFavorite = async (id) => {
    if (!isCustomer()) return;
    try {
      const res = await api.customerToggleFavorite('series', id);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.favorited) next.add(id); else next.delete(id);
        return next;
      });
    } catch (e) { alert('Favorite failed: ' + (e.response?.data?.error || e.message)); }
  };
  const toggleSeries = async (id, currentStatus) => {
    try {
      await isCustomer() ? api.customerToggleSeries(id) : api.updateSeries(id, { enabled: currentStatus ? 0 : 1 });
      loadData();
    } catch (error) {
      alert('Failed to toggle series: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === series.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(series.map(s => s.id));
    }
  };

  const bulkEnableSelected = async () => {
    if (selectedIds.length === 0) { alert('Please select items first'); return; }
    if (!window.confirm(`Enable ${selectedIds.length} selected series?`)) return;
    try { await isCustomer() ? api.customerBulkToggleSeries(selectedIds, true) : api.bulkUpdateSeries({ series_ids: selectedIds, updates: { enabled: 1 } }); loadData(); }
    catch (error) { alert('Bulk enable failed: ' + (error.response?.data?.error || error.message)); }
  };

  const bulkDisableSelected = async () => {
    if (selectedIds.length === 0) { alert('Please select items first'); return; }
    if (!window.confirm(`Disable ${selectedIds.length} selected series?`)) return;
    try { await isCustomer() ? api.customerBulkToggleSeries(selectedIds, false) : api.bulkUpdateSeries({ series_ids: selectedIds, updates: { enabled: 0 } }); loadData(); }
    catch (error) { alert('Bulk disable failed: ' + (error.response?.data?.error || error.message)); }
  };

  const deleteSeries = async (id, name) => {
    if (!window.confirm(`Delete series "${name}"?`)) return;
    try { await api.deleteSeries(id); loadData(); }
    catch (error) { alert('Delete failed: ' + (error.response?.data?.error || error.message)); }
  };

    const enableProviderContent = async (providerId, providerName, enable) => {
    const action = enable ? 'Enable' : 'Disable';
    if (!window.confirm(`${action} ALL series from provider "${providerName}"?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleSeriesByFilter({ enabled: enable, provider_id: providerId });
      } else {
        if (enable) { await api.enableProviderContent(providerId); }
        else { await api.disableProviderContent(providerId); }
      }
      alert(`${action}d all series from ${providerName}`);
      loadData();
    } catch (error) { alert(`Provider ${action.toLowerCase()} failed: ` + (error.response?.data?.error || error.message)); }
  };

  const bulkDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert('Please select items first');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.length} selected series?`)) {
      return;
    }

    try {
      if (isCustomer()) {
        await api.customerBulkToggleSeries(selectedIds.map(String), false);
        alert('Series removed from your view');
      } else {
        await api.bulkDeleteSeries({ series_ids: selectedIds });
      }
      loadData();
    } catch (error) {
      alert('Bulk delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

    const bulkEnableByCategory = async () => {
    if (!filters.categories.length) { alert('Please select at least one category first'); return; }
    if (!window.confirm(`Enable all series in ${filters.categories.length} selected categor${filters.categories.length === 1 ? "y" : "ies"}?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleSeriesByFilter({ enabled: true, categories: filters.categories, ...(filters.provider_id ? { provider_id: filters.provider_id } : {}) });
      } else {
        const params = { categories: filters.categories.join(',') };
        if (filters.provider_id) { params.provider_id = filters.provider_id; }
        const res = await api.bulkEnableSeries(params);
        alert(res.data.message);
      }
      loadData();
    } catch (error) { alert('Bulk enable failed: ' + (error.response?.data?.error || error.message)); }
  };

    const bulkDisableByCategory = async () => {
    if (!filters.categories.length) { alert('Please select at least one category first'); return; }
    if (!window.confirm(`Disable all series in ${filters.categories.length} selected categor${filters.categories.length === 1 ? "y" : "ies"}?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleSeriesByFilter({ enabled: false, categories: filters.categories, ...(filters.provider_id ? { provider_id: filters.provider_id } : {}) });
      } else {
        const params = { categories: filters.categories.join(',') };
        if (filters.provider_id) { params.provider_id = filters.provider_id; }
        const res = await api.bulkDisableSeries(params);
        alert(res.data.message);
      }
      loadData();
    } catch (error) { alert('Bulk disable failed: ' + (error.response?.data?.error || error.message)); }
  };

  const nextPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  const prevPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  };

  if (loading) {
    return <div className="loading">Loading series...</div>;
  }

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div>
      <div className="page-header">
        <h2>TV Series</h2>
        <p>Manage your TV shows and series content</p>
      </div>

      <div className="table-container" style={{ marginBottom: '20px' }}>
        <div className="table-header">
          <h3>Filters</h3>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Provider</label>
            <select
              value={filters.provider_id}
              onChange={(e) => handleFilterChange('provider_id', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="">All Providers</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <CategoryMultiSelect
              categories={categories}
              selected={filters.categories}
              onChange={(next) => handleFilterChange('categories', next)}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Status</label>
            <select
              value={filters.enabled}
              onChange={(e) => handleFilterChange('enabled', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Search</label>
            <input
              type="text"
              placeholder="Search series..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={applyFilters} style={{ width: '100%' }}>
              <Filter size={16} />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#f0f9ff', border: '1px solid #0ea5e9' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>{selectedIds.length} series selected</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={bulkEnableSelected}>
                Enable Selected
              </button>
              <button className="btn btn-secondary" onClick={bulkDisableSelected}>
                Disable Selected
              </button>
              <button className="btn btn-danger" onClick={bulkDeleteSelected}>
                <Trash2 size={16} />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Bulk Actions */}
      {filters.categories.length > 0 && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#fef3c7', border: '1px solid #f59e0b' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Bulk actions for {filters.categories.length} categor{filters.categories.length === 1 ? 'y' : 'ies'}: {filters.categories.join(', ')}</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={bulkEnableByCategory}>
                Enable All in Category
              </button>
              <button className="btn btn-secondary" onClick={bulkDisableByCategory}>
                Disable All in Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Bulk Actions */}
      {filters.provider_id && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#e0e7ff', border: '1px solid #6366f1' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Bulk actions for provider: {providers.find(p => p.id === parseInt(filters.provider_id))?.name || filters.provider_id}</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={() => enableProviderContent(filters.provider_id, providers.find(p => p.id === parseInt(filters.provider_id))?.name || '', true)}>
                Enable All Provider Series
              </button>
              <button className="btn btn-secondary" onClick={() => enableProviderContent(filters.provider_id, providers.find(p => p.id === parseInt(filters.provider_id))?.name || '', false)}>
                Disable All Provider Series
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>
            <Tv size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            TV Series ({pagination.total})
          </h3>
        </div>

        {series.length === 0 ? (
          <div className="empty-state">
            <h3>No series found</h3>
            <p>Sync a provider to see series content here</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <button
                      onClick={toggleSelectAll}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {selectedIds.length === series.length ? (
                        <CheckSquare size={20} color="#0ea5e9" />
                      ) : (
                        <Square size={20} color="#9ca3af" />
                      )}
                    </button>
                  </th>
                  <th>Series</th>
                  <th>Category</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {series.map((show) => (
                  <tr key={show.id}>
                    <td>
                      <button
                        onClick={() => toggleSelection(show.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        {selectedIds.includes(show.id) ? (
                          <CheckSquare size={20} color="#0ea5e9" />
                        ) : (
                          <Square size={20} color="#9ca3af" />
                        )}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {show.cover && (
                          <img src={show.cover} alt="" style={{ width: '40px', height: '60px', borderRadius: '4px', objectFit: 'cover' }} />
                        )}
                        {isCustomer() && (
                        <button onClick={() => toggleFavorite(show.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title="Add to My List">
                          <Star size={16} fill={favoriteIds.has(show.id) ? '#f59e0b' : 'none'} color={favoriteIds.has(show.id) ? '#f59e0b' : '#9ca3af'} />
                        </button>
                      )}
                      <strong>{show.name}</strong>
                      </div>
                    </td>
                    <td>{show.category || 'Uncategorized'}</td>
                    <td>{show.provider_name}</td>
                    <td>
                      <span className={`badge badge-${show.enabled ? 'success' : 'danger'}`}>
                        {show.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleSeries(show.id, show.enabled)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        {show.enabled ? (
                          <ToggleRight size={24} color="#10b981" />
                        ) : (
                          <ToggleLeft size={24} color="#9ca3af" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteSeries(show.id, show.name)}
                        className="btn btn-danger btn-sm"
                        title="Delete Series"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e5e7eb' }}>
              <div>
                <label style={{ fontSize: '13px', color: '#6b7280', marginRight: '12px' }}>
                  Per page{' '}
                  <select
                    value={pagination.limit}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </label>
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={prevPage}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <div style={{ padding: '8px 16px', background: '#f3f4f6', borderRadius: '6px' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={nextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Series;
